import { Box, Button, ButtonGroup, Flex, FormLabel, Input } from "@chakra-ui/react";

export type DateFilterMode = "on" | "range" | "gte" | "lte";

export interface DateFilterValue {
    mode: DateFilterMode;
    date?: string;
    from?: string;
    to?: string;
}

/** Returns true if the filter has enough data to actually send to the backend. */
export function isDateFilterActive(v: DateFilterValue | undefined): boolean {
    if (!v) return false;
    if (v.mode === "range") return !!(v.from || v.to);
    return !!v.date;
}

const MODES: { value: DateFilterMode; label: string }[] = [
    { value: "on", label: "On" },
    { value: "range", label: "Between" },
    { value: "lte", label: "On or before" },
    { value: "gte", label: "On or after" },
];

interface DateFilterProps {
    label: string;
    value: DateFilterValue | undefined;
    onChange: (value: DateFilterValue | undefined) => void;
    isActive?: boolean;
}

export function DateFilter({ label, value, onChange, isActive }: DateFilterProps) {
    const mode: DateFilterMode = value?.mode ?? "on";

    function setMode(m: DateFilterMode) {
        onChange({ mode: m });
    }

    function setDate(d: string) {
        onChange({ mode, date: d || undefined });
    }

    function setFrom(d: string) {
        onChange({ mode: "range", from: d || undefined, to: value?.to });
    }

    function setTo(d: string) {
        onChange({ mode: "range", from: value?.from, to: d || undefined });
    }

    return (
        <Flex direction="column" gap={1}>
            <FormLabel fontSize="xs" mb={0} display="flex" alignItems="center" gap={1}>
                {label}
                {isActive && <Box boxSize="6px" borderRadius="full" bg="blue.500" aria-label="Filter active" />}
            </FormLabel>
            <ButtonGroup size="xs" isAttached variant="outline">
                {MODES.map(m => (
                    <Button
                        key={m.value}
                        colorScheme={mode === m.value ? "blue" : "gray"}
                        variant={mode === m.value ? "solid" : "outline"}
                        onClick={() => setMode(m.value)}
                    >
                        {m.label}
                    </Button>
                ))}
            </ButtonGroup>
            {mode === "range" ? (
                <Flex gap={1} direction="column">
                    <Input type="date" size="sm" placeholder="From" value={value?.from ?? ""} onChange={e => setFrom(e.target.value)} />
                    <Input type="date" size="sm" placeholder="To" value={value?.to ?? ""} onChange={e => setTo(e.target.value)} />
                </Flex>
            ) : (
                <Input type="date" size="sm" value={value?.date ?? ""} onChange={e => setDate(e.target.value)} />
            )}
        </Flex>
    );
}
